/**
 * MH-01 Tổng quan điều hành — cho người dùng biết trong vòng năm giây rằng hệ
 * thống đang bình thường hay có vấn đề, và vấn đề nằm ở đâu.
 *
 * Bố cục theo bản mẫu V.dashboard:
 *  - hàng trên: card "Số bản tin đã xuất bản" cạnh hai hàng DeviceStat/
 *    ConnectionStat (Cụm loa truyền thanh, Bảng điện tử);
 *  - hàng dưới: card "Bản đồ số" (dải lọc) + cột phải (tóm tắt kết nối hai nhóm,
 *    danh sách thiết bị);
 *  - hàng cuối: tình trạng tích hợp và top địa bàn mất kết nối (giữ từ bản trước).
 *
 * Nguồn dữ liệu, tất cả là API thật:
 *  - `GET /dashboard/summary` — thời điểm tính, tích hợp, top địa bàn (bộ đệm, QĐ-17);
 *  - `GET /devices?loaiThietBi=…&trangThaiHienThi=…&pageSize=1` — đếm theo loại
 *    thiết bị (backend chưa có tóm tắt theo loại, xem `taiTomTatThietBi`);
 *  - `GET /broadcast/logs?tuThoiDiem=<0 giờ hôm nay>&pageSize=1` — lượt phát trong ngày
 *    của cụm loa;
 *  - `GET /bulletins/statistics?nguonId=…` — số bản tin đã xuất bản, tổng và theo
 *    năm loại của Công văn 276 (không có bản tin FM);
 *  - `GET /devices/map` — bản đồ gom nhóm vẽ trên nền OpenStreetMap (`BanDoNen`);
 *    `GET /devices` — danh sách cột phải.
 * Phần chưa có API (bản tin đã phát của bảng điện tử) hiện đúng bố cục, số "—" và
 * nhãn Chưa có API.
 *
 * Mọi số liệu thiết bị bấm được và mở danh sách với bộ lọc tương ứng. Làm mới 60 giây.
 *
 * Khối địa bàn mất kết nối xếp theo TỶ LỆ, không theo số tuyệt đối, để địa bàn nhỏ
 * mất kết nối toàn bộ không bị che bởi địa bàn lớn (Bảng 4.1).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viThietBi } from '../ngon-ngu/vi-thiet-bi';
import { thietBiApi, type NhomXaBanDo, type NutDiaBan, type SoLieuLoaiBanDo } from '../api/thiet-bi-api';
import { phatSongApi } from '../api/phat-song-api';
import { banTinApi, type ThongKeBanTin } from '../api/ban-tin-api';
import { bangTinApi } from '../api/bang-tin-api';
import { BanDoNen, type DiemBanDo } from '../thanh-phan/BanDoNen';
import { dungTaiDuLieu, dungTuLamMoi, type KetQuaTai } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi, TrangThaiRong, TrangThaiRongLoc } from '../thanh-phan/TrangThaiManHinh';
import { gioThanhChu } from '../thanh-phan/NhanTrangThai';
import { NhanChuaCoApi, TheTag, TruongLoc, type KieuTag } from '../thanh-phan/MauGiaoDien';
import { BieuTuong } from '../thanh-phan/BieuTuong';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import {
  BieuTuongThietBi,
  ChonDonViHanhChinh,
  ChuGiaiPhat,
  soHienThi,
  taiTomTatThietBi,
  type LoaiThietBi,
  type TomTatThietBi,
} from './MH02DanhSachThietBi';
import './thiet-bi.css';

interface SoLieu {
  thietBi: { tong: number; hoatDong: number; matKetNoi: number; chuaKichHoat: number };
  topDiaBanMatKetNoi: { nguonId: string; ten: string | null; soMat: number; soTong: number }[];
  /** Mọi địa bàn có thiết bị: đang kết nối / tổng, tách cụm loa và bảng tin (máy chủ mới). */
  danhSachDiaBan?: {
    nguonId: string;
    ten: string;
    loaKetNoi: number;
    loaTong: number;
    bangKetNoi: number;
    bangTong: number;
  }[];
  tichHop: {
    nhaCungCap: {
      ma_nha_cung_cap: string;
      ten: string;
      so_bo_tuong_thich: string;
      so_thiet_bi_truc_tiep?: string;
      so_cum_loa_truc_tiep?: string;
      so_bang_tin_truc_tiep?: string;
      so_thiet_bi_gian_tiep?: string;
    }[];
    hangDoiChieuRa: number;
    soThatBai: number;
    guiCuoiLuc: number | null;
    guiTrungUongBat?: boolean;
  };
  tinhLuc: number;
}

/** Thẻ "Danh sách thiết bị" chỉ hiện các xã/phường nhiều thiết bị nhất; xem đủ ở trang Cụm loa. */
const SO_DIA_BAN_TOI_DA = 6;

/** Tên địa bàn trong danh mục có thể viết thường ("xã Sông Lô"): viết hoa ký tự đầu khi hiển thị. */
function vietHoaChuDau(s: string): string {
  return s ? s.charAt(0).toLocaleUpperCase('vi') + s.slice(1) : s;
}

type DuLieuBanDo = Awaited<ReturnType<typeof thietBiApi.banDo>>;

/** Tông tag của từng loại bản tin (theo mã loại) — dùng các tag đã có trong bảng màu. */
const TAG_LOAI_BAN_TIN: Record<keyof ThongKeBanTin['theoLoai'], KieuTag> = {
  '1': 'xanh',
  '2': 'vang',
  '3': 'ok',
  '4': 'vang',
  '5': 'do',
};

/** Epoch giây của 0 giờ hôm nay theo giờ máy người dùng. */
function dauNgayHomNay(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
}

/* --------------------------- Hàng DeviceStat ------------------------------ */

function HangThietBi({
  loai,
  tomTat,
  soPhat,
  dataVung,
}: {
  loai: LoaiThietBi;
  tomTat: KetQuaTai<TomTatThietBi>;
  /** `null` = chưa có API. */
  soPhat: KetQuaTai<number> | null;
  dataVung: string;
}): ReactNode {
  const t = viThietBi.tongQuan;
  const coLoi = tomTat.trangThai === 'loi';
  const d = tomTat.duLieu;
  const goc = loai === 'cum_thu_phat_thanh' ? '/thiet-bi' : '/bang-tin-dien-tu';
  const tenNhom = loai === 'cum_thu_phat_thanh' ? t.cumLoaTruyenThanh : t.bangDienTu;
  const bieuTuong = (
    <span className="tq-o__bieu-tuong" aria-hidden="true">
      <BieuTuongThietBi loai={loai} co={36} />
    </span>
  );

  return (
    <section className="tq-the" data-loai={loai} aria-label={tenNhom}>
      <ul className="tq-o-so" data-vung={dataVung} aria-label={vi.tongQuan.ghiChuO}>
        <li data-nhom="phat-trong-ngay">
          <div className="tq-o">
            {bieuTuong}
            <span>
              <span className="tq-o__nhan" title={soPhat ? t.banTinDaPhatGhiChu : undefined}>
                {t.banTinDaPhatTrongNgay}
                {soPhat ? null : <NhanChuaCoApi />}
              </span>
              {soPhat ? (
                <Link
                  className="tq-o__so"
                  to={loai === 'cum_thu_phat_thanh' ? '/nghe-lai' : '/bang-tin-dien-tu/nhat-ky-ban-tin'}
                >
                  {soHienThi(soPhat.duLieu ?? undefined, soPhat.trangThai === 'loi')}
                </Link>
              ) : (
                <strong className="tq-o__so">{viThietBi.chung.chuaCo}</strong>
              )}
            </span>
          </div>
        </li>
        <li data-nhom="tong">
          <div className="tq-o">
            {bieuTuong}
            <span>
              <Link className="tq-lien-ket-o" to={goc}>
                <span className="tq-o__nhan">{tenNhom}</span>
                <strong className="tq-o__so">{soHienThi(d?.tong, coLoi)}</strong>
              </Link>
            </span>
          </div>
        </li>
        <li data-nhom="ket-noi">
          <div className="tq-ket-noi">
            <span>{viThietBi.tomTat.dangKetNoi}</span>
            <span className="tq-ket-noi__dong">
              <strong className="tq-ket-noi__so">{soHienThi(d?.dangKetNoi, coLoi)}</strong>
              <ChuGiaiPhat
                dangPhat={d?.dangPhat}
                khongPhat={d?.khongPhat}
                coLoi={coLoi}
                lienKetDangPhat={`${goc}?trangThaiHienThi=dang_phat`}
              />
            </span>
          </div>
        </li>
        <li data-nhom="mat-ket-noi">
          <Link className="tq-ket-noi" to={`${goc}?trangThaiHienThi=mat_ket_noi`}>
            <span>{viThietBi.tomTat.matKetNoi}</span>
            <strong className="tq-ket-noi__so">{soHienThi(d?.matKetNoi, coLoi)}</strong>
          </Link>
        </li>
      </ul>
    </section>
  );
}

/* ------------------------------- Bản đồ số -------------------------------- */

/** Thẻ thông tin xã khi rê chuột lên điểm bản đồ — dựng bằng DOM, chữ qua `textContent`. */
function dungTheXa(x: NhomXaBanDo, luc: number): HTMLElement {
  const t = viThietBi.tongQuan;
  const tao = (the: string, lop?: string, chu?: string): HTMLElement => {
    const el = document.createElement(the);
    if (lop) el.className = lop;
    if (chu !== undefined) el.textContent = chu;
    return el;
  };
  const cap = (xanh: number, doSo: number): HTMLElement => {
    const el = tao('span', 'the-xa__cap');
    el.append(
      tao('span', 'the-xa__xanh', String(xanh)),
      tao('span', 'the-xa__vach'),
      tao('span', 'the-xa__do', String(doSo)),
    );
    return el;
  };
  const khoi = (tieuDe: string, so: SoLieuLoaiBanDo, lop: string): HTMLElement => {
    const el = tao('div', `the-xa__khoi ${lop}`);
    const dau = tao('div', 'the-xa__khoi-dau');
    dau.append(tao('span', undefined, `${tieuDe}:`), tao('span', 'the-xa__tong', String(so.tong)));
    const ketNoi = tao('div', 'the-xa__dong');
    ketNoi.append(tao('span', undefined, `${t.theXaKetNoi}:`), cap(so.ketNoi, so.matKetNoi));
    const hoatDong = tao('div', 'the-xa__dong');
    hoatDong.append(tao('span', undefined, `${t.theXaHoatDong}:`), cap(so.dangPhat, so.khongPhat));
    el.append(dau, ketNoi, hoatDong);
    return el;
  };
  const the = tao('div', 'the-xa');
  const ma = tao('p', 'the-xa__ma', `${t.theXaMa}:`);
  ma.append(tao('b', undefined, x.nguonId));
  const d = new Date(luc);
  const hai = (n: number): string => String(n).padStart(2, '0');
  the.append(
    tao('p', 'the-xa__ten', x.ten),
    ma,
    khoi(t.theXaLoa, x.loa, 'the-xa__khoi--loa'),
    khoi(t.theXaBang, x.bang, 'the-xa__khoi--bang'),
    tao(
      'p',
      'the-xa__cap-nhat',
      `${t.theXaCapNhat}: ${hai(d.getHours())}:${hai(d.getMinutes())}:${hai(d.getSeconds())} ngày ${hai(d.getDate())}/${hai(d.getMonth() + 1)}/${d.getFullYear()}`,
    ),
  );
  return the;
}

interface NhomDiem {
  viDo: number;
  kinhDo: number;
  soThietBi: number;
  soMatKetNoi: number;
}

/**
 * Vẽ bản đồ gom nhóm trên nền OpenStreetMap (thành phần dùng chung với MH-05).
 * Nhóm còn thiết bị mất kết nối vẽ ô vuông màu mất kết nối; nhóm toàn bộ đang kết
 * nối vẽ hình tròn — hình dạng khác nhau nên màu không là phương tiện duy nhất.
 */
function VeBanDo({ duLieu }: { duLieu: DuLieuBanDo }): ReactNode {
  const t = viThietBi.tongQuan;
  const diem: DiemBanDo[] = useMemo(() => {
    // Một điểm mỗi xã/phường; rê chuột hiện thẻ thông tin xã.
    if (duLieu.nhomXa && duLieu.nhomXa.length > 0) {
      const luc = Date.now();
      return duLieu.nhomXa.map((x) => ({
        khoa: x.nguonId,
        viDo: x.viDo,
        kinhDo: x.kinhDo,
        soThietBi: x.soThietBi,
        soMatKetNoi: x.soMatKetNoi,
        tieuDe: x.ten,
        theThongTin: () => dungTheXa(x, luc),
      }));
    }
    const nhom: NhomDiem[] =
      duLieu.nhom.length > 0
        ? duLieu.nhom
        : duLieu.diem
            .filter((x) => x.viDo !== null && x.kinhDo !== null)
            .map((x) => ({
              viDo: x.viDo as number,
              kinhDo: x.kinhDo as number,
              soThietBi: 1,
              soMatKetNoi: x.trangThaiHienThi === 'mat_ket_noi' ? 1 : 0,
            }));
    return nhom.map((n, i) => ({
      khoa: String(i),
      ...n,
      tieuDe: dienVao(t.nhomThietBi, { so: n.soThietBi, mat: n.soMatKetNoi }),
    }));
  }, [duLieu, t.nhomThietBi]);

  if (diem.length === 0) return <div className="tq-ban-do__trong">{t.banDoRong}</div>;
  return <BanDoNen diem={diem} nhan={t.banDoSo} tuCanKhung />;
}

/* ================================= MH-01 ================================== */

export function MH01TongQuan(): ReactNode {
  const phien = dungPhien();
  const t = viThietBi.tongQuan;
  const nguonPhien = phien.nguonIdHienTai;

  const kq = dungTaiDuLieu<SoLieu>({
    tai: (signal) => thietBiApi.tongQuan(nguonPhien, signal) as unknown as Promise<SoLieu>,
    // Tổng quan LUÔN hiện đủ bố cục, kể cả khi địa bàn chưa có thiết bị: số 0
    // cũng là thông tin điều hành. Từng khối con tự hiện trạng thái rỗng riêng.
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [nguonPhien],
  });

  const tomTatLoa = dungTaiDuLieu<TomTatThietBi>({
    tai: (signal) => taiTomTatThietBi(thietBiApi.danhSach, 'cum_thu_phat_thanh', nguonPhien, signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [nguonPhien],
  });
  const tomTatBang = dungTaiDuLieu<TomTatThietBi>({
    // Trạng thái bảng tin là DV016 (bang-tin-gateway), không phải DV008 của cụm loa.
    tai: async (signal) => ({
      ...(await bangTinApi.tomTatThietBi({ nguonId: nguonPhien }, signal)),
      chuaKichHoat: 0,
    }),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [nguonPhien],
  });
  const soPhatLoa = dungTaiDuLieu<number>({
    tai: (signal) =>
      phatSongApi
        .ngheLai({ page: 1, pageSize: 1, nguonId: nguonPhien, tuThoiDiem: String(dauNgayHomNay()) }, signal)
        .then((d) => d.total),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [nguonPhien],
  });

  /** Lượt phát bản tin trên bảng tin từ 0 giờ hôm nay (nhật ký DV013). */
  const soPhatBang = dungTaiDuLieu<number>({
    tai: (signal) =>
      bangTinApi
        .nhatKyPhat({ page: 1, pageSize: 1, nguonId: nguonPhien, tuThoiDiem: dauNgayHomNay() }, signal)
        .then((d) => d.total),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [nguonPhien],
  });

  // Số bản tin đã xuất bản theo địa bàn trang đang lọc (rỗng = cả tỉnh).
  const thongKeBanTin = dungTaiDuLieu<ThongKeBanTin>({
    tai: (signal) => banTinApi.thongKe({ nguonId: nguonPhien }, signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [nguonPhien],
  });

  /* Bộ lọc bản đồ số */
  const [locDonVi, datLocDonVi] = useState('');
  const [locTrangThai, datLocTrangThai] = useState('');
  const [locLoai, datLocLoai] = useState<'' | LoaiThietBi>('');
  const nguonBanDo = locDonVi || nguonPhien;

  const cay = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => thietBiApi.cayDiaBan({}, signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });
  const banDo = dungTaiDuLieu<DuLieuBanDo>({
    // Không truyền khung nhìn: lấy toàn bộ phạm vi, mức 10 để máy chủ gom nhóm.
    tai: (signal) => thietBiApi.banDo({ mucThuPhong: 10, nguonId: nguonBanDo, gomTheo: 'xa' }, signal),
    laRong: (d) => d.nhom.length === 0 && d.diem.length === 0 && (d.nhomXa ?? []).length === 0,
    dangLoc: true,
    phuThuoc: [nguonBanDo],
  });

  // Làm mới 60 giây. Tổng quan không có chọn dòng nên chỉ dừng khi tab ẩn.
  dungTuLamMoi(
    () => {
      kq.taiLai();
      tomTatLoa.taiLai();
      tomTatBang.taiLai();
      soPhatLoa.taiLai();
      thongKeBanTin.taiLai();
      banDo.taiLai();
    },
    { dangChonDong: false, dangMoNgan: false },
  );

  if (kq.trangThai === 'dang-tai') return <KhungXuong soDong={4} soCot={4} taiLau={kq.taiLau} />;
  if (kq.trangThai === 'loi' && kq.loi) return <TrangThaiLoi loi={kq.loi} onThuLai={kq.taiLai} />;
  if (kq.trangThai === 'rong') return <TrangThaiRong />;
  if (kq.trangThai === 'rong-loc') {
    return <TrangThaiRongLoc onXoaLoc={() => phien.datPhamViDangChon([])} />;
  }
  const d = kq.duLieu;
  if (!d) return <TrangThaiRong />;

  return (
    <section className="man-hinh man-hinh--luoi tong-quan" data-man-hinh="MH-01">
      <header className="man-hinh__dau">
        <h1>{vi.tongQuan.tieuDe}</h1>
      </header>

      {/* Hàng trên: bản tin đã xuất bản + hai hàng thiết bị. */}
      <div className="tq-hang">
        <section className="tq-the tq-xuat-ban" data-vung="ban-tin-da-xuat-ban">
          <p className="tq-xuat-ban__tieu-de">{t.banTinDaXuatBan}</p>
          <p className="tq-xuat-ban__so" data-vung="tong-ban-tin">
            {soHienThi(thongKeBanTin.duLieu?.tong, thongKeBanTin.trangThai === 'loi')}
          </p>
          <ul>
            {t.loaiBanTin.map(([ma, nhan]) => (
              <li key={ma} data-loai-ban-tin={ma}>
                <TheTag kieu={TAG_LOAI_BAN_TIN[ma]}>{nhan}</TheTag>
                <b>{soHienThi(thongKeBanTin.duLieu?.theoLoai[ma], thongKeBanTin.trangThai === 'loi')}</b>
              </li>
            ))}
          </ul>
        </section>
        <div className="tq-nhom">
          <HangThietBi loai="cum_thu_phat_thanh" tomTat={tomTatLoa} soPhat={soPhatLoa} dataVung="o-so-lieu" />
          <HangThietBi
            loai="bang_tin_dien_tu"
            tomTat={tomTatBang}
            soPhat={soPhatBang}
            dataVung="o-so-lieu-bang-dien-tu"
          />
        </div>
      </div>

      {/* Hàng dưới: bản đồ số + cột phải. */}
      <div className="tq-hang tq-hang--duoi">
        <section className="tq-the tq-the--lon" data-vung="ban-do-so">
          <h2>{t.banDoSo}</h2>
          <div className="tq-ban-do__loc">
            <ChonDonViHanhChinh
              id="tq-loc-don-vi"
              giaTri={locDonVi}
              cay={cay.duLieu ?? []}
              onDoi={datLocDonVi}
            />
            <TruongLoc nhan={t.trangThaiKetNoi} htmlFor="tq-loc-trang-thai">
              <select
                id="tq-loc-trang-thai"
                value={locTrangThai}
                onChange={(e) => datLocTrangThai(e.target.value)}
              >
                <option value="">{viThietBi.chung.tatCa}</option>
                {Object.entries(vi.trangThaiThietBi).map(([ma, nhan]) => (
                  <option key={ma} value={ma}>
                    {nhan}
                  </option>
                ))}
              </select>
            </TruongLoc>
            <TruongLoc nhan={t.loaiThietBi} htmlFor="tq-loc-loai">
              <select
                id="tq-loc-loai"
                value={locLoai}
                onChange={(e) => datLocLoai(e.target.value as '' | LoaiThietBi)}
              >
                <option value="">{viThietBi.chung.tatCa}</option>
                <option value="cum_thu_phat_thanh">{t.cumLoa}</option>
                <option value="bang_tin_dien_tu">{t.bangDienTu}</option>
              </select>
            </TruongLoc>
          </div>
          <div className="tq-ban-do__nen">
            {banDo.trangThai === 'dang-tai' && !banDo.duLieu ? (
              <div className="tq-ban-do__trong">{viThietBi.chung.dangTai}</div>
            ) : banDo.trangThai === 'loi' && banDo.loi ? (
              <TrangThaiLoi loi={banDo.loi} onThuLai={banDo.taiLai} />
            ) : banDo.duLieu ? (
              <VeBanDo duLieu={banDo.duLieu} />
            ) : null}
          </div>
          <ul className="tq-ban-do__chu-giai">
            <li>
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <circle cx="6" cy="6" r="5" fill="var(--online-solid)" />
              </svg>
              {t.chuGiaiDangKetNoi}
            </li>
            <li>
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <rect x="1" y="1" width="10" height="10" fill="var(--offline-solid)" />
              </svg>
              {t.chuGiaiCoMatKetNoi}
            </li>
            {banDo.duLieu && banDo.duLieu.soKhongCoToaDo > 0 ? (
              <li>
                {t.khongCoToaDo}: {banDo.duLieu.soKhongCoToaDo}
              </li>
            ) : null}
            <li>
              <Link to="/thiet-bi/ban-do">{vi.thietBi.banDo}</Link>
            </li>
          </ul>
        </section>

        <div className="tq-cot-phai">
          <section className="tq-the tq-ket-noi-nhom" data-vung="tom-tat-ket-noi">
            {(
              [
                ['cum_thu_phat_thanh', t.cumLoaTruyenThanh, tomTatLoa],
                ['bang_tin_dien_tu', t.bangTinDienTuCongCong, tomTatBang],
              ] as const
            ).map(([loai, ten, tt]) => (
              <div key={loai}>
                <h3>{ten}</h3>
                <dl>
                  <div>
                    <dt>{viThietBi.tomTat.dangKetNoi}</dt>
                    <dd className="tq-so--ket-noi">
                      {soHienThi(tt.duLieu?.dangKetNoi, tt.trangThai === 'loi')}
                    </dd>
                  </div>
                  <div>
                    <dt>{viThietBi.tomTat.matKetNoi}</dt>
                    <dd className="tq-so--mat">{soHienThi(tt.duLieu?.matKetNoi, tt.trangThai === 'loi')}</dd>
                  </div>
                </dl>
              </div>
            ))}
          </section>

          <section className="tq-the tq-danh-sach" data-vung="danh-sach-thiet-bi">
            {/* Tiêu đề mở trang Cụm loa truyền thanh (danh sách đầy đủ). */}
            <h3>
              <Link to="/thiet-bi" className="tq-danh-sach__tieu-de">
                {t.danhSachThietBi}
              </Link>
            </h3>
            {!kq.duLieu ? (
              <div className="bang-trong">{viThietBi.chung.dangTai}</div>
            ) : (kq.duLieu.danhSachDiaBan ?? []).length === 0 ? (
              <div className="bang-trong">{t.khongCoThietBi}</div>
            ) : (
              <ul className="tq-ds-dia-ban">
                {(kq.duLieu.danhSachDiaBan ?? []).slice(0, SO_DIA_BAN_TOI_DA).map((x) => (
                  <li key={x.nguonId} data-dia-ban={x.nguonId}>
                    <Link
                      to={`/thiet-bi?nguonId=${encodeURIComponent(x.nguonId)}`}
                      className="tq-ds-dia-ban__ten"
                    >
                      {x.ten}
                    </Link>
                    <span
                      className="tq-ds-dia-ban__so"
                      title={dienVao(t.soLoaGoiY, { ket: x.loaKetNoi, tong: x.loaTong })}
                    >
                      <BieuTuong ten="loa" co={16} />
                      <b className="tq-so--ket-noi">{x.loaKetNoi}</b>/
                      <b className="tq-ds-dia-ban__tong">{x.loaTong}</b>
                    </span>
                    <span
                      className="tq-ds-dia-ban__so"
                      title={dienVao(t.soBangGoiY, { ket: x.bangKetNoi, tong: x.bangTong })}
                    >
                      <BieuTuong ten="man-hinh" co={16} />
                      <b className="tq-so--ket-noi">{x.bangKetNoi}</b>/
                      <b className="tq-ds-dia-ban__tong">{x.bangTong}</b>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {/* Hàng cuối: tình trạng tích hợp và top địa bàn mất kết nối. */}
      <div className="tq-hang tq-hang--hai">
        <section className="tq-the" data-vung="tinh-trang-tich-hop">
          <h2>{vi.tongQuan.tichHopTieuDe}</h2>
          <h3 className="tq-the__tieu-de-phu">{vi.tongQuan.guiTrungUong}</h3>
          <p>
            {vi.tongQuan.congTacGui}:{' '}
            <TheTag kieu={d.tichHop.guiTrungUongBat ? 'xanh' : 'vang'}>
              {d.tichHop.guiTrungUongBat ? vi.tongQuan.congTacBat : vi.tongQuan.congTacTat}
            </TheTag>
          </p>
          <p>
            {vi.tongQuan.hangDoiChieuRa}: <strong>{d.tichHop.hangDoiChieuRa}</strong> ·{' '}
            {vi.tongQuan.goiTinThatBai}: <strong>{d.tichHop.soThatBai}</strong> · {vi.tongQuan.guiCuoi}:{' '}
            <strong>{d.tichHop.guiCuoiLuc ? gioThanhChu(d.tichHop.guiCuoiLuc) : vi.tongQuan.chuaGui}</strong>
          </p>
          <h3 className="tq-the__tieu-de-phu">{vi.tongQuan.nhaCungCapTieuDe}</h3>
          <ul className="khoi__ncc">
            {d.tichHop.nhaCungCap.map((n) => {
              const btt = Number(n.so_bo_tuong_thich ?? 0);
              const tt = Number(n.so_thiet_bi_truc_tiep ?? 0);
              const ttLoa = Number(n.so_cum_loa_truc_tiep ?? tt);
              const ttBang = Number(n.so_bang_tin_truc_tiep ?? 0);
              const gt = Number(n.so_thiet_bi_gian_tiep ?? 0);
              const phan = [
                ...(btt > 0 || gt > 0 ? [dienVao(vi.tongQuan.nccQuaBtt, { btt, so: gt })] : []),
                ...(ttLoa > 0 ? [dienVao(vi.tongQuan.nccTrucTiep, { so: ttLoa })] : []),
                ...(ttBang > 0 ? [dienVao(vi.tongQuan.nccBangTinTrucTiep, { so: ttBang })] : []),
              ];
              return (
                <li key={n.ma_nha_cung_cap}>
                  {n.ten}: {phan.length > 0 ? phan.join(' · ') : vi.tongQuan.nccChuaCoThietBi}
                </li>
              );
            })}
          </ul>
          {/* Cầu dao của nhà cung cấp thuộc phần giám sát của đợt sau; dải thông
            báo trong trang sẽ được nối vào đây khi có dữ liệu cầu dao. */}
        </section>

        <section className="tq-the" data-vung="top-dia-ban">
          <h2>{vi.tongQuan.topTieuDe}</h2>
          {d.topDiaBanMatKetNoi.length === 0 ? (
            <p>{vi.trangThaiManHinh.rongTieuDe}</p>
          ) : (
            <ul>
              {d.topDiaBanMatKetNoi.map((x) => (
                <li key={x.nguonId}>
                  {vietHoaChuDau(x.ten ?? x.nguonId)}{' '}
                  <strong>
                    {x.soMat}/{x.soTong}
                  </strong>{' '}
                  <Link to={`/thiet-bi?nguonId=${x.nguonId}&trangThaiHienThi=mat_ket_noi`}>
                    {vi.tongQuan.xem}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </section>
  );
}
